import { Directive, ElementRef, forwardRef, inject, input, output } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControlValueAccessor } from '~core/abstractions/base-control-value-accessor';
import { NumberInputService } from '~core/services/number-input.service';
import { OptionalType } from '~shared/core/types/optional.type';

/**
 * Put on any input element (with type="text"!) to make
 * it behave like all other number inputs.
 */
@Directive({
    selector: 'input[arcNumberInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberInputDirective),
            multi: true
        }
    ],
    host: {
        '[attr.min]': 'min()',
        '[attr.max]': 'max()',
        '[attr.inputmode]': 'decimalPlaces() === 0 ? "numeric" : "decimal"',
        '(change)': 'onValueChanged($event)'
    }
})
export class NumberInputDirective extends BaseControlValueAccessor<number> {
    readonly min = input<number>();
    readonly max = input<number>();
    readonly decimalPlaces = input<number>();
    readonly minDecimalPlaces = input<number>();
    readonly isCurrency = input<boolean>();

    readonly arcNumberInputValueChanged = output<OptionalType<number>>();

    private readonly elementRef = inject(ElementRef<HTMLInputElement>);
    private readonly numberInputService = inject(NumberInputService);

    override writeValue(value?: number | undefined): void {
        // undefined must be a valid value, else it would cause infinite recursion
        if (this.isValueValid(value) || value === undefined) {
            this.value = value;
            const [valueStr] = this.parse(value);
            this.elementRef.nativeElement.value = valueStr ?? '';
        } else {
            this.value = undefined;
            this.elementRef.nativeElement.value = '';
        }
    }

    override valueChanged(newValue?: number, shouldMarkAsTouched = true): void {
        super.valueChanged(newValue, shouldMarkAsTouched);
        this.arcNumberInputValueChanged.emit(newValue);
    }

    /**
     * After the user has finished typing,
     * write the formatted value back to the input field
     */
    onValueChanged(event: Event): void {
        const input = event.target as HTMLInputElement;
        const [valueStr, value] = this.parse(input.value);
        this.valueChanged(value);
        input.value = valueStr ?? '';
    }

    private parse(value: OptionalType<string | number>) {
        return this.numberInputService.parse(value, {
            min: this.min(),
            max: this.max(),
            decimalPlaces: this.decimalPlaces(),
            minDecimalPlaces: this.minDecimalPlaces(),
            isCurrency: this.isCurrency()
        });
    }
}
